<script lang="ts">
  import { Link } from 'svelte-routing';
  import Bars from '../charts/Bars.svelte';
  import Chart from '../charts/Chart.svelte';
  import Dots from '../charts/Dots.svelte';
  import Readout from '../charts/Readout.svelte';
  import { api } from '../../api/client';
  import type { Activity } from '../../api/types';
  import { activityIcon, clockTime } from '../../utils/activities';
  import { average7, dateTicks, daysBetween, niceTicks, polyline, segments, yPct } from '../../utils/chart';
  import { calendar, shiftDay, today, type LogWeek } from '../../utils/insights';
  import { weekStart } from '../../utils/progress';
  import { FORM_BANDS, formBand, type LoadDay } from '../../utils/training';
  import { date, locale, month, monthDay, t } from '../../i18n/index.ts';

  // TrainingLoadSection is Training load on Progress (spec 006 FR-090):
  // fitness, fatigue, and form over 3 months, 6 months, or a year, each
  // day's load as a bar under them, hollow where it was estimated without
  // heart rate, and resting heart rate and HRV from Health history on the
  // same dates when kept. In the shared chart language (spec 010
  // 010 FR-033) each has its own scale; form's bands are shaded and named
  // behind the lines, the days before the oldest kept activity are shaded
  // as having none, and resting heart rate and HRV are faint daily points
  // under their 7-day averages. Hovering reads a day; tapping one, or the
  // arrow keys, selects it and lists its activities.
  const RANGES = [
    { days: 91, name: t('activitiesLoad3Months') },
    { days: 182, name: t('activitiesLoad6Months') },
    { days: 365, name: t('activitiesLoad1Year') },
  ];
  // Blue, peach, and mauve read apart in both themes; form is also dashed.
  const FITNESS = 'var(--theme-blue)';
  const FATIGUE = 'var(--theme-peach)';
  const FORM = 'var(--theme-mauve)';
  const LOAD = 'var(--theme-overlay2)';
  const REST = 'var(--theme-red)';
  const HRV = 'var(--theme-teal)';
  // BAND_COLORS follow FORM_BANDS' order: fresh, neutral, productive, overreaching.
  const BAND_COLORS = ['var(--theme-green)', 'var(--theme-overlay0)', 'var(--theme-blue)', 'var(--theme-red)'];
  // MAIN_REM is the main chart's height, so a band's name shows only where
  // the band is tall enough to hold it.
  const MAIN_REM = 12;
  const ws = weekStart(navigator.language);

  let span = $state(91);
  let days = $state<LoadDay[]>([]);
  let since = $state<string | undefined>(undefined);
  let loaded = $state(false);
  let at = $state<number | null>(null);
  let hover = $state<number | null>(null);
  let acts = $state<Activity[] | null>(null);
  let error = $state('');

  $effect(() => {
    const n = span;
    const to = today();
    at = null;
    api.get<{ days: LoadDay[]; since?: string }>(`/me/training-load?from=${shiftDay(to, -(n - 1))}&to=${to}&${calendar()}`)
      .then((got) => { days = got.days; since = got.since; loaded = true; error = ''; })
      .catch((e) => (error = e.message));
  });

  $effect(() => {
    const d = at != null ? days[at]?.date : null;
    acts = null;
    if (!d) return;
    api.get<LogWeek>(`/me/log?${calendar()}&week=${d}`)
      .then((w) => { if (at != null && days[at]?.date === d) acts = w.days.find((x) => x.day === d)?.activities ?? []; })
      .catch(() => {});
  });

  let n = $derived(days.length);
  let positions = $derived(days.map((_, i) => (n > 1 ? i / (n - 1) : 0.5)));
  let main = $derived(niceTicks(Math.min(0, ...days.map((d) => d.form)), Math.max(5, ...days.flatMap((d) => [d.fitness, d.fatigue, d.form]))));
  let load = $derived(niceTicks(0, Math.max(0, ...days.map((d) => d.load)), { zero: true, whole: true, max: 3 }));
  let rest = $derived(days.map((d) => d.resting_hr ?? null));
  let hrv = $derived(days.map((d) => d.hrv ?? null));
  let rest7 = $derived(average7(rest));
  let hrv7 = $derived(average7(hrv));
  let restScale = $derived(rangeScale(rest));
  let hrvScale = $derived(rangeScale(hrv));
  let hasRest = $derived(rest.some((v) => v != null));
  let hasHrv = $derived(hrv.some((v) => v != null));
  let estimated = $derived(days.some((d) => d.estimated));

  // bare is how far across the days before the oldest kept activity reach,
  // all of it when none is kept.
  let bare = $derived(n < 2 ? 0 : !since ? 1 : Math.max(0, Math.min(1, daysBetween(days[0].date, since) / (n - 1))));

  function rangeScale(vs: (number | null)[]) {
    const got = vs.filter((v): v is number => v != null);
    return got.length ? niceTicks(Math.min(...got), Math.max(...got), { max: 4 }) : niceTicks(0, 1);
  }

  let xTicks = $derived.by(() => {
    if (n < 2) return [];
    const ticks = dateTicks(days[0].date, days[n - 1].date, 6, ws);
    return ticks.days.map((d) => {
      const on = new Date(`${d}T12:00:00`);
      return { at: daysBetween(days[0].date, d) / (n - 1), text: ticks.unit === 'month' ? month(on, true) : monthDay(on) };
    });
  });

  const line = (f: (d: LoadDay) => number) => polyline(days.map((d, i) => [positions[i], f(d)] as [number, number]), main.lo, main.hi);
  const avgLines = (vs: (number | null)[], lo: number, hi: number) => segments(vs.map((v, i) => ({ at: positions[i], v }))).map((sg) => polyline(sg, lo, hi));

  // bands are form's bands within the scale, each with its top and bottom
  // down the chart in percent.
  let bands = $derived(FORM_BANDS.map((b, k) => ({
    name: b.name,
    color: BAND_COLORS[k],
    top: yPct(Math.min(main.hi, b.hi), main.lo, main.hi),
    bottom: yPct(Math.max(main.lo, b.lo), main.lo, main.hi),
  })).filter((b) => b.bottom > b.top));

  const r1 = (v: number) => String(Math.round(v));
  const signed = (v: number) => (Math.round(v) > 0 ? `+${Math.round(v)}` : Math.round(v) < 0 ? `−${Math.abs(Math.round(v))}` : '0');
  const dayFormat = new Intl.DateTimeFormat(locale(), { weekday: 'short', month: 'short', day: 'numeric' });
  const dayName = (d: string) => dayFormat.format(new Date(`${d}T12:00:00`));
  let shown = $derived(hover ?? at);
  let i = $derived(shown ?? n - 1);
  let cur = $derived(days[i]);
  let hrvName = $derived(days.find((d) => d.hrv_kind)?.hrv_kind === 'sdnn' ? t('activitiesLoadHrvSdnn') : t('activitiesLoadHrv'));
  let sinceName = $derived(since ? date(new Date(`${since}T12:00:00`)) : '');
  let valueText = $derived.by(() => {
    if (!cur) return '';
    const args = { day: dayName(cur.date), fitness: r1(cur.fitness), fatigue: r1(cur.fatigue), form: signed(cur.form), band: formBand(cur.form).toLowerCase(), load: r1(cur.load) };
    return cur.estimated ? t('activitiesLoadValueTextEstimated', args) : t('activitiesLoadValueText', args);
  });

  // daily is a once-a-day measure's readout: the day's value and its 7-day average.
  function daily(v: number | null, avg: number | null, unit: 'bpm' | 'ms'): string {
    const value = v == null ? '–' : unit === 'bpm' ? t('activitiesBpm', { n: r1(v) }) : t('activitiesMs', { n: r1(v) });
    return avg != null ? t('activitiesLoadWithAverage', { value, avg: r1(avg) }) : value;
  }

  // loadText is an activity's load in the chosen day's list.
  function loadText(a: Activity): string {
    if (a.load == null) return '';
    const load = Math.round(a.load);
    return a.load_estimated ? t('activitiesLoadValueEstimated', { load }) : t('activitiesLoadValue', { load });
  }

  let bandsNote = $derived(bare > 0 ? (since ? t('activitiesLoadBandsSince', { date: sinceName }) : t('activitiesLoadBandsNoneKept')) : t('activitiesLoadBands'));
</script>

{#snippet hatch()}
  {#if bare > 0}
    <span class="absolute inset-y-0 left-0 pointer-events-none" aria-hidden="true"
      style="width: {bare * 100}%; background: repeating-linear-gradient(135deg, color-mix(in srgb, var(--theme-overlay0) 30%, transparent) 0 1px, transparent 1px 7px);"></span>
  {/if}
{/snippet}

<section class="card p-4 space-y-3" aria-labelledby="load-heading">
  <div class="flex flex-wrap items-center justify-between gap-2">
    <h2 id="load-heading" class="font-semibold">{t('activitiesLoad')}</h2>
    <div class="flex gap-1" role="group" aria-label={t('activitiesPeriod')}>
      {#each RANGES as rg (rg.days)}
        <button class="btn btn-sm {span === rg.days ? 'btn-primary' : 'btn-secondary'}" aria-pressed={span === rg.days} onclick={() => (span = rg.days)}>{rg.name}</button>
      {/each}
    </div>
  </div>
  {#if error}
    <p class="text-sm" style="color: var(--theme-error);">{error}</p>
  {/if}
  {#if loaded && n === 0}
    <p class="text-sm text-text-muted">{t('activitiesLoadNone')}</p>
  {:else if cur}
    <Readout title={shown == null ? t('activitiesLoadToday') : dayName(cur.date)}
      items={[
        { label: t('activitiesLoadFitness'), value: r1(cur.fitness), color: FITNESS },
        { label: t('activitiesLoadFatigue'), value: r1(cur.fatigue), color: FATIGUE },
        { label: t('activitiesLoadForm'), value: t('activitiesLoadFormBand', { form: signed(cur.form), band: formBand(cur.form) }), color: FORM, mark: 'dash' },
        { label: t('activitiesLoadDaily'), value: cur.estimated ? t('activitiesLoadEstimated', { load: r1(cur.load) }) : r1(cur.load), color: LOAD, mark: cur.estimated ? 'hollow' : 'bar' },
        ...(hasRest ? [{ label: t('activitiesLoadRestingHr'), value: daily(rest[i], rest7[i], 'bpm'), color: REST }] : []),
        ...(hasHrv ? [{ label: hrvName, value: daily(hrv[i], hrv7[i], 'ms'), color: HRV }] : []),
      ]} />
    <div class="space-y-3">
      <Chart lo={main.lo} hi={main.hi} yTicks={main.ticks.map((tick) => ({ v: tick, text: signed(tick) }))} {xTicks} xLabels={false}
        height="h-48" {positions} bind:at bind:hover label={t('activitiesLoadChartMain')} {valueText}
        dots={[{ v: cur.form, color: FORM }, { v: cur.fatigue, color: FATIGUE }, { v: cur.fitness, color: FITNESS }]}>
        {#snippet plot()}
          {#each bands as b (b.name)}
            <rect x="0" width="100" y={b.top} height={b.bottom - b.top} fill={b.color} fill-opacity="0.12" />
          {/each}
          <line x1="0" x2="100" y1={yPct(0, main.lo, main.hi)} y2={yPct(0, main.lo, main.hi)} stroke="var(--theme-overlay0)" stroke-width="1" vector-effect="non-scaling-stroke" />
          <polyline points={line((d) => d.form)} fill="none" stroke={FORM} stroke-width="2" stroke-dasharray="5 4" vector-effect="non-scaling-stroke" />
          <polyline points={line((d) => d.fatigue)} fill="none" stroke={FATIGUE} stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
          <polyline points={line((d) => d.fitness)} fill="none" stroke={FITNESS} stroke-width="2.5" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
        {/snippet}
        {#snippet overlay()}
          {@render hatch()}
          {#each bands as b (b.name)}
            {#if ((b.bottom - b.top) / 100) * MAIN_REM >= 1.1}
              <span class="absolute left-1 leading-none pointer-events-none text-text rounded-sm px-1 py-0.5" style="top: calc({b.top}% + 2px); background: color-mix(in srgb, var(--theme-card-bg) 80%, transparent);" aria-hidden="true">{b.name}</span>
            {/if}
          {/each}
          {#if bare > 0}
            <span class="absolute bottom-1 leading-tight pointer-events-none text-text text-center"
              style="left: 0; width: {bare * 100}%; max-width: calc(100% - 0.5rem);" aria-hidden="true"><span class="rounded-sm px-1" style="background: color-mix(in srgb, var(--theme-card-bg) 80%, transparent);">{t('activitiesLoadNoneKept')}</span></span>
          {/if}
        {/snippet}
      </Chart>
      <div>
        <p class="text-xs text-text-muted mb-0.5">{t('activitiesLoadDailyHeading')}</p>
        <Chart lo={load.lo} hi={load.hi} yTicks={load.ticks.map((tick) => ({ v: tick, text: String(tick) }))} {xTicks} xLabels={!hasRest && !hasHrv}
          height="h-16" {positions} bind:at bind:hover label={t('activitiesLoadChartLoad')} {valueText}>
          {#snippet overlay()}
            {@render hatch()}
            <Bars bars={days.map((d, k) => ({ at: positions[k], v: d.load, color: LOAD, hollow: d.estimated })).filter((b) => b.v > 0)} width={0.7 / Math.max(1, n)} lo={load.lo} hi={load.hi} />
          {/snippet}
        </Chart>
      </div>
      {#if hasRest}
        <div>
          <p class="text-xs text-text-muted mb-0.5">{t('activitiesLoadRestHeading')}</p>
          <Chart lo={restScale.lo} hi={restScale.hi} unit={t('activitiesUnitBpm')} yTicks={restScale.ticks.map((tick) => ({ v: tick, text: String(tick) }))} {xTicks} xLabels={!hasHrv}
            height="h-20" {positions} bind:at bind:hover label={t('activitiesLoadChartRest')} {valueText}
            dots={rest7[i] != null ? [{ v: rest7[i]!, color: REST }] : []}>
            {#snippet plot()}
              {#each avgLines(rest7, restScale.lo, restScale.hi) as l, k (k)}
                <polyline points={l} fill="none" stroke={REST} stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
              {/each}
            {/snippet}
            {#snippet overlay()}
              <Dots dots={rest.map((v, k) => ({ at: positions[k], v }))} color={REST} lo={restScale.lo} hi={restScale.hi} faint />
            {/snippet}
          </Chart>
        </div>
      {/if}
      {#if hasHrv}
        <div>
          <p class="text-xs text-text-muted mb-0.5">{t('activitiesLoadHrvHeading')}</p>
          <Chart lo={hrvScale.lo} hi={hrvScale.hi} unit={t('activitiesUnitMs')} yTicks={hrvScale.ticks.map((tick) => ({ v: tick, text: String(tick) }))} {xTicks}
            height="h-20" {positions} bind:at bind:hover label={t('activitiesLoadChartHrv')} {valueText}
            dots={hrv7[i] != null ? [{ v: hrv7[i]!, color: HRV }] : []}>
            {#snippet plot()}
              {#each avgLines(hrv7, hrvScale.lo, hrvScale.hi) as l, k (k)}
                <polyline points={l} fill="none" stroke={HRV} stroke-width="2" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
              {/each}
            {/snippet}
            {#snippet overlay()}
              <Dots dots={hrv.map((v, k) => ({ at: positions[k], v }))} color={HRV} lo={hrvScale.lo} hi={hrvScale.hi} faint />
            {/snippet}
          </Chart>
        </div>
      {/if}
    </div>
    <p class="text-xs text-text-muted">
      {bandsNote}
      {estimated ? t('activitiesLoadBarsEstimated') : t('activitiesLoadBars')}
    </p>
    {#if at != null && acts}
      <ul class="space-y-1">
        {#each acts as a (a.id)}
          <li>
            <Link to="/activity/{a.id}" class="flex items-center gap-2 text-sm hover:opacity-80">
              <i aria-hidden="true" class="las {activityIcon(a.type)}"></i><span class="flex-1">{a.name}</span>
              <span class="text-text-muted">{loadText(a)}{a.timed ? ` · ${clockTime(a.moving_s)}` : ''}</span>
            </Link>
          </li>
        {:else}
          <li class="text-sm text-text-muted">{t('activitiesLoadNoneThatDay')}</li>
        {/each}
      </ul>
    {/if}
  {/if}
</section>
